import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaLeaf,
  FaCertificate,
  FaBarcode,
  FaDownload,
  FaGlassWhiskey,
  FaTemperatureHigh,
  FaAppleAlt,
  FaBoxOpen,
  FaSnowflake,
  FaShieldAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const CannedBottledFruits = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // ✅ Update products array with data sheets
  const products = [
    {
      key: "jackfruit",
      images: [
        "/images/products/Sweet-jackfruit-pieces-bottle.webp",
        "/images/products/Sweet-jackfruit-pieces-serving.webp",
      ],
      image: "🍈",
      color: "from-yellow-400 to-amber-500",
      dataSheet: "/images/DataSheet/jackfruit-canned.jpg",
    },
    {
      key: "fruitCocktail",
      images: [
        "/images/products/fruit-cocktail-jar.webp",
        "/images/products/fruit-cocktail-bowl.webp",
      ],
      image: "🍹",
      color: "from-pink-400 to-orange-500",
      dataSheet: "/images/DataSheet/fruit-cocktail-canned.jpg",
    },
    {
      key: "mangoChunks",
      images: [
        "/images/products/mango-chunks-can.webp",
        "/images/products/mango-chunks-plate.webp",
      ],
      image: "🥭",
      color: "from-orange-400 to-yellow-500",
      dataSheet: "/images/DataSheet/mango-chunks-canned.jpg",
    },
    {
      key: "pineappleRings",
      images: [
        "/images/products/pineapple-rings-jar.webp",
        "/images/products/pineapple-rings-display.webp",
      ],
      image: "🍍",
      color: "from-yellow-300 to-yellow-600",
      dataSheet: "/images/DataSheet/pineapple-rings-canned.jpg",
    },
    {
      key: "pineappleChunks",
      images: [
        "/images/products/pineapple-chunks-can.webp",
        "/images/products/pineapple-chunks-fresh.webp",
      ],
      image: "🍍",
      color: "from-yellow-400 to-amber-600",
      dataSheet: "/images/DataSheet/pineapple-chunks-canned.jpg",
    },
  ];

  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-orange-600 to-pink-600 text-white p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t("dehydratedFruits.downloadModal.title")}
                </h3>
                <p className="text-orange-100 text-sm">
                  {t("dehydratedFruits.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List - Scrollable */}
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4">
                    {/* Product Icon */}
                    <div
                      className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-3xl shadow-md`}
                    >
                      {product.image}
                    </div>

                    {/* Product Info */}
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                        {t(`cannedBottledFruits.products.${product.key}.name`)}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t("dehydratedFruits.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-orange-600 text-white rounded-full p-3 group-hover:bg-orange-700 group-hover:scale-110 transition-all duration-300 shadow-md">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const certifications = [
    {
      name: t("dehydratedFruits.certifications.brc.name"),
      image: "/images/certifications/BRC.webp",
      alt: t("dehydratedFruits.certifications.brc.name"),
      description: t("dehydratedFruits.certifications.brc.description"),
      icon: "🏆",
    },
    {
      name: t("dehydratedFruits.certifications.eu.name"),
      image: "/images/certifications/EU.webp",
      alt: t("dehydratedFruits.certifications.eu.name"),
      description: t("dehydratedFruits.certifications.eu.description"),
      icon: "🇪🇺",
    },
    {
      name: t("dehydratedFruits.certifications.usda.name"),
      image: "/images/certifications/USDA.webp",
      alt: t("dehydratedFruits.certifications.usda.name"),
      description: t("dehydratedFruits.certifications.usda.description"),
      icon: "🇺🇸",
    },
    {
      name: t("dehydratedFruits.certifications.jas.name"),
      image: "/images/certifications/JAS.webp",
      alt: t("dehydratedFruits.certifications.jas.name"),
      description: t("dehydratedFruits.certifications.jas.description"),
      icon: "🇯🇵",
    },
    {
      name: t("dehydratedFruits.certifications.naturland.name"),
      image: "/images/certifications/Naturland.webp",
      alt: t("dehydratedFruits.certifications.naturland.name"),
      description: t("dehydratedFruits.certifications.naturland.description"),
      icon: "🌱",
    },
    {
      name: t("dehydratedFruits.certifications.fairtrade.name"),
      image: "/images/certifications/FairTrade.webp",
      alt: t("dehydratedFruits.certifications.fairtrade.name"),
      description: t("dehydratedFruits.certifications.fairtrade.description"),
      icon: "🤝",
    },
    {
      name: t("dehydratedFruits.certifications.fda.name"),
      image: "/images/certifications/FDA.webp",
      alt: t("dehydratedFruits.certifications.fda.name"),
      description: t("dehydratedFruits.certifications.fda.description"),
      icon: "✅",
    },
  ];

  const commonFeatures = [
    {
      icon: <FaAppleAlt />,
      titleKey: "premiumSelection",
    },
    {
      icon: <FaShieldAlt />,
      titleKey: "safeProcessing",
    },
    {
      icon: <FaBoxOpen />,
      titleKey: "flexiblePackaging",
    },
    {
      icon: <FaLeaf />,
      titleKey: "certifiedOrganic",
    },
  ];

  const qualityItems = [
    {
      icon: <FaShieldAlt />,
      key: "brcCompliant",
    },
    {
      icon: <FaBarcode />,
      key: "traceability",
    },
    {
      icon: <FaCertificate />,
      key: "verifiedQuality",
    },
  ];

  const servingItems = [
    {
      icon: <FaGlassWhiskey />,
      key: "readyToEat",
    },
    {
      icon: <FaTemperatureHigh />,
      key: "afterOpening",
    },
    {
      icon: <FaSnowflake />,
      key: "storageTips",
    },
  ];

  const faqKeys = ["sugar", "organic", "privateLabel", "shelfLife"];

  // Reset image index when product changes
  const handleProductChange = (index) => {
    setActiveProduct(index);
    setActiveImageIndex(0);
  };

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  const getProductBestUses = (productKey) => {
    return [
      t(`cannedBottledFruits.products.${productKey}.bestUses.1`),
      t(`cannedBottledFruits.products.${productKey}.bestUses.2`),
      t(`cannedBottledFruits.products.${productKey}.bestUses.3`),
      t(`cannedBottledFruits.products.${productKey}.bestUses.4`),
    ];
  };

  return (
    <>
      {/* SEO Component */}
      <SEO
        title="Organic Canned & Bottled Fruits | Premium Jackfruit, Mango, Pineapple & Fruit Cocktail"
        description="Certified organic canned and bottled fruits from Sri Lanka. Premium jackfruit, mango chunks, pineapple rings, and fruit cocktail in organic juice. BRC certified, no added sugars, 24-month shelf life. Available in glass jars and cans."
        keywords="organic canned fruits, organic bottled fruits, organic jackfruit canned, organic mango chunks, organic pineapple rings, organic fruit cocktail, canned fruits no sugar added, BRC certified canned fruits, USDA organic canned fruits, EU organic fruits, organic pineapple juice, Sri Lankan canned fruits, wholesale organic canned fruits, private label canned fruits, organic preserved fruits, natural canned fruits, traceable organic fruits, certified organic canned goods, organic tropical fruits, glass jar fruits"
        image="https://worganaturals.com/images/products/Sweet-jackfruit-pieces-bottle.webp"
        url="/products/canned-bottled-fruits"
        type="product"
        productSchema={true}
        category="Organic Canned & Preserved Fruits"
        brand="Worga Naturals"
        availability="InStock"
        price="4.99"
        currency="USD"
        sku="CBF-JACK-350G"
      />

      {/* Additional Structured Data */}
      <Helmet>
        {/* Product Collection Schema with Variants */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Organic Canned & Bottled Fruits Collection",
            description:
              "Hand-selected organic fruits from Sri Lanka, packed in glass jars and cans. Includes jackfruit, mango chunks, pineapple rings & chunks, and fruit cocktail. BRC certified with no added sugars, colors, or preservatives. 24-month shelf life.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
            },
            image: [
              "https://worganaturals.com/images/products/Sweet-jackfruit-pieces-bottle.webp",
              "https://worganaturals.com/images/products/fruit-cocktail-jar.webp",
              "https://worganaturals.com/images/products/mango-chunks-can.webp",
              "https://worganaturals.com/images/products/pineapple-rings-jar.webp",
              "https://worganaturals.com/images/products/pineapple-chunks-can.webp",
            ],
            category: "Organic Canned & Preserved Fruits",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "4.99",
              highPrice: "19.99",
              offerCount: "5",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              reviewCount: "189",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "24 months",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "350g glass jar, 400ml (A1) can, 3kg (A10) can",
              },
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value:
                  "BRC Food Safety, EU Organic, USDA Organic, JAS, Naturland, Fairtrade",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka",
              },
              {
                "@type": "PropertyValue",
                name: "Processing",
                value: "Exhausted, sterilized, HACCP & GMP certified",
              },
              {
                "@type": "PropertyValue",
                name: "Free From",
                value: "No added sugars, colors, flavoring, preservatives",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Organic Sweet Jackfruit Pieces in Pineapple Juice",
                description:
                  "Premium organic jackfruit pieces in organic pineapple juice. Yellow color with typical sweet jackfruit aroma and flavor. Soft and juicy texture. Perfect for desserts, fruit salads, and yogurt toppings.",
                sku: "CBF-JACK-350G",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Yellow colour pieces",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft and juicy",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredients",
                    value: "Organic Sweet Jackfruit, Organic Pineapple Juice",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "5.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Fruit Cocktail in Pineapple Juice",
                description:
                  "Colorful mix of organic pineapple (40%), papaya (20%), mango (20%), and watermelon (20%) in organic pineapple juice. Fleshy and soft texture. Ideal for party bowls, desserts, and ice cream toppings.",
                sku: "CBF-COCKTAIL-350G",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value:
                      "Reddish and yellowish pieces in light yellow infusion",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Fleshy and soft",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredients",
                    value:
                      "Organic pineapple 40%, papaya 20%, mango 20%, watermelon 20%",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "6.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Mango Chunks in Pineapple Juice",
                description:
                  "Premium organic TJC mango chunks in organic pineapple juice. Yellow pieces with characteristic mango aroma and flavor. Soft texture. Perfect for smoothies, salsas, desserts, and baking.",
                sku: "CBF-MANGO-350G",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Yellow pieces in light yellow infusion",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredients",
                    value: "Organic TJC mango, Organic Pineapple Juice",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "5.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Pineapple Rings in Own Juice",
                description:
                  "100% organic pineapple rings in their own juice. Uniform yellow rings with typical pineapple aroma and flavor. Soft yet intact rings. Ideal for pizzas, cakes, grilling, and catering platters.",
                sku: "CBF-PINE-RINGS-350G",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Uniform yellow rings in light yellow infusion",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft yet intact rings",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredients",
                    value: "Organic pineapple 100% (own juice)",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "4.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
              {
                "@type": "Product",
                name: "Organic Pineapple Chunks in Own Juice",
                description:
                  "100% organic pineapple chunks in their own juice. Yellow pieces with characteristic pineapple aroma and flavor. Soft texture. Perfect for salads, stir-fries, desserts, and food service.",
                sku: "CBF-PINE-CHUNKS-350G",
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Appearance",
                    value: "Yellow pieces in light yellow infusion",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Texture",
                    value: "Soft",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Ingredients",
                    value: "Organic pineapple 100% (own juice)",
                  },
                ],
                offers: {
                  "@type": "Offer",
                  price: "4.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                },
              },
            ],
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is sugar added to Worga Naturals canned fruits?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No—no added sugars, colors, flavors, or preservatives. Our canned and bottled fruits contain only organic fruit and organic juice where applicable. They are naturally sweet with no artificial additives.",
                },
              },
              {
                "@type": "Question",
                name: "Are Worga Naturals canned fruits truly organic?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—all our canned and bottled fruits are certified by EU Organic, USDA (NOP), JAS, and Naturland. Every batch is fully traceable from farm to package. We maintain an identity-preserved organic supply chain.",
                },
              },
              {
                "@type": "Question",
                name: "Can I get private label canned fruits?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes—custom labels and pack sizes are available. We offer private label services for glass jars and cans in various sizes (350g, 400ml, 3kg). Contact us for bulk and private label inquiries.",
                },
              },
              {
                "@type": "Question",
                name: "What is the shelf life of canned fruits?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "24 months from date of manufacture in unopened containers. After opening, refrigerate and consume within 3-5 days for best quality and freshness.",
                },
              },
              {
                "@type": "Question",
                name: "What packaging options are available?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We offer three main packaging options: 350g glass jars for retail, 400ml (A1) cans for food service, and 3kg (A10) cans for bulk/institutional use. Custom sizes are also available upon request.",
                },
              },
              {
                "@type": "Question",
                name: "How are the fruits processed?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Each product is carefully sorted, filled with organic juice where applicable, exhausted to remove air, capped or seamed securely, and sterilized in BRC-compliant facilities following HACCP and GMP protocols to ensure safety and extended shelf life.",
                },
              },
            ],
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Canned & Bottled Fruits",
                item: "https://worganaturals.com/products/canned-bottled-fruits",
              },
            ],
          })}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Premium organic products from Sri Lanka including canned fruits, spices, coconut water, and natural preserves. BRC certified with full traceability.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-XX-XXXXXXX",
              contactType: "Customer Service",
              availableLanguage: ["en", "si"],
            },
            sameAs: [
              "https://www.facebook.com/worganaturals",
              "https://www.instagram.com/worganaturals",
              "https://www.linkedin.com/company/worganaturals",
            ],
          })}
        </script>

        {/* ItemList Schema for Product Collection */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Organic Canned & Bottled Fruits Collection",
            description: "Premium organic preserved fruits from Sri Lanka",
            numberOfItems: 5,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Product",
                  name: "Organic Sweet Jackfruit Pieces in Pineapple Juice",
                  url: "https://worganaturals.com/products/canned-bottled-fruits#jackfruit",
                  image:
                    "https://worganaturals.com/images/products/Sweet-jackfruit-pieces-bottle.webp",
                  offers: {
                    "@type": "Offer",
                    price: "5.99",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Product",
                  name: "Organic Fruit Cocktail in Pineapple Juice",
                  url: "https://worganaturals.com/products/canned-bottled-fruits#fruit-cocktail",
                  image:
                    "https://worganaturals.com/images/products/fruit-cocktail-jar.webp",
                  offers: {
                    "@type": "Offer",
                    price: "6.49",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Product",
                  name: "Organic Mango Chunks in Pineapple Juice",
                  url: "https://worganaturals.com/products/canned-bottled-fruits#mango-chunks",
                  image:
                    "https://worganaturals.com/images/products/mango-chunks-can.webp",
                  offers: {
                    "@type": "Offer",
                    price: "5.49",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Product",
                  name: "Organic Pineapple Rings in Own Juice",
                  url: "https://worganaturals.com/products/canned-bottled-fruits#pineapple-rings",
                  image:
                    "https://worganaturals.com/images/products/pineapple-rings-jar.webp",
                  offers: {
                    "@type": "Offer",
                    price: "4.99",
                    priceCurrency: "USD",
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 5,
                item: {
                  "@type": "Product",
                  name: "Organic Pineapple Chunks in Own Juice",
                  url: "https://worganaturals.com/products/canned-bottled-fruits#pineapple-chunks",
                  image:
                    "https://worganaturals.com/images/products/pineapple-chunks-can.webp",
                  offers: {
                    "@type": "Offer",
                    price: "4.99",
                    priceCurrency: "USD",
                  },
                },
              },
            ],
          })}
        </script>

        {/* Recipe Schema - Example using Pineapple */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Recipe",
            name: "Tropical Fruit Salad with Organic Canned Fruits",
            description:
              "Quick and healthy tropical fruit salad using Worga Naturals organic canned fruits",
            image:
              "https://worganaturals.com/images/recipes/tropical-fruit-salad.webp",
            recipeIngredient: [
              "1 jar Organic Pineapple Chunks",
              "1 jar Organic Mango Chunks",
              "1 jar Organic Fruit Cocktail",
              "Fresh mint leaves",
              "Lime juice (optional)",
            ],
            recipeInstructions: [
              {
                "@type": "HowToStep",
                text: "Drain the organic canned fruits and combine in a large bowl",
              },
              {
                "@type": "HowToStep",
                text: "Add fresh mint leaves and optional lime juice",
              },
              {
                "@type": "HowToStep",
                text: "Chill for 30 minutes and serve",
              },
            ],
            prepTime: "PT10M",
            totalTime: "PT40M",
            recipeYield: "4 servings",
            recipeCategory: "Dessert",
            recipeCuisine: "International",
            keywords:
              "tropical fruit salad, organic canned fruits, healthy dessert",
          })}
        </script>

        {/* Video Schema - Product Overview (if you have video) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "Worga Naturals Organic Canned Fruits - Product Overview",
            description:
              "Discover our premium organic canned and bottled fruits from Sri Lanka",
            thumbnailUrl:
              "https://worganaturals.com/images/video-thumbnails/canned-fruits-overview.webp",
            uploadDate: "2024-01-15",
            duration: "PT2M30S",
            contentUrl:
              "https://worganaturals.com/videos/canned-fruits-overview.mp4",
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />
        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-orange-600 to-pink-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("cannedBottledFruits.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("cannedBottledFruits.hero.title.part1")}{" "}
              </span>
              <span className="text-orange-600 dark:text-orange-500">
                {t("cannedBottledFruits.hero.title.part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("cannedBottledFruits.hero.description")}
            </p>

            {/* Traceability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg"
            >
              <FaBarcode className="text-orange-600 text-2xl" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("cannedBottledFruits.hero.traceability")}
              </span>
            </motion.div>
          </motion.div>
        </section>

        {/* Common Features */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("cannedBottledFruits.features.title")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {commonFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {t(
                      `cannedBottledFruits.features.${feature.titleKey}.title`
                    )}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t(`cannedBottledFruits.features.${feature.titleKey}.desc`)}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Key Highlights */}
            <div className="bg-gradient-to-r from-orange-600 to-pink-600 rounded-2xl p-8 text-white shadow-2xl">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    {t("cannedBottledFruits.highlights.productHighlights")}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <FaCheckCircle className="text-orange-200 text-xl mt-1 flex-shrink-0" />
                      <span>
                        {t("cannedBottledFruits.highlights.shelfLife")}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <FaCheckCircle className="text-orange-200 text-xl mt-1 flex-shrink-0" />
                      <span>
                        {t("cannedBottledFruits.highlights.freeFrom")}
                      </span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    {t("cannedBottledFruits.highlights.availableSizes")}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <FaCheckCircle className="text-orange-200 text-xl mt-1 flex-shrink-0" />
                      <span>
                        {t("cannedBottledFruits.highlights.size350g")}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <FaCheckCircle className="text-orange-200 text-xl mt-1 flex-shrink-0" />
                      <span>
                        {t("cannedBottledFruits.highlights.size400ml")}
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <FaCheckCircle className="text-orange-200 text-xl mt-1 flex-shrink-0" />
                      <span>{t("cannedBottledFruits.highlights.size3kg")}</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <FaCheckCircle className="text-orange-200 text-xl mt-1 flex-shrink-0" />
                      <span>
                        {t("cannedBottledFruits.highlights.customSizes")}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Product Family */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("cannedBottledFruits.products.title")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => handleProductChange(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-orange-600 to-pink-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  <span className="hidden md:inline">
                    {t(`cannedBottledFruits.products.${product.key}.name`)}
                  </span>
                  <span className="md:hidden">
                    {product.image}{" "}
                    {t(`cannedBottledFruits.products.${product.key}.name`)
                      .split(" ")[1]
                      .substring(0, 10)}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProduct}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
              >
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Product Image Slider */}
                  <div
                    className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                  >
                    {/* Image Display */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeImageIndex}
                        initial={{ opacity: 0, scale: 0.9, x: 100 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: -100 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="w-full h-full flex items-center justify-center"
                      >
                        <img
                          src={products[activeProduct].images[activeImageIndex]}
                          alt={`${t(
                            `cannedBottledFruits.products.${products[activeProduct].key}.name`
                          )} - View ${activeImageIndex + 1}`}
                          className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                          onError={(e) => {
                            e.target.style.display = "none";
                            const fallback = document.createElement("div");
                            fallback.className = "text-9xl";
                            fallback.textContent =
                              products[activeProduct].image;
                            e.target.parentElement.appendChild(fallback);
                          }}
                        />
                      </motion.div>
                    </AnimatePresence>

                    {/* Navigation Arrows */}
                    {products[activeProduct].images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                          aria-label="Previous image"
                        >
                          <FaChevronLeft className="text-orange-600 text-2xl" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm"
                          aria-label="Next image"
                        >
                          <FaChevronRight className="text-orange-600 text-2xl" />
                        </button>
                      </>
                    )}

                    {/* Image Indicators */}
                    {products[activeProduct].images.length > 1 && (
                      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2">
                        {products[activeProduct].images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveImageIndex(index)}
                            className={`transition-all duration-300 rounded-full ${
                              activeImageIndex === index
                                ? "bg-white w-10 h-3"
                                : "bg-white/50 hover:bg-white/75 w-3 h-3"
                            }`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Image Counter */}
                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {activeImageIndex + 1} /{" "}
                      {products[activeProduct].images.length}
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="p-8 md:p-12">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                      {t(
                        `cannedBottledFruits.products.${products[activeProduct].key}.name`
                      )}
                    </h3>

                    {/* Sensory Profile */}
                    <div className="space-y-4 mb-6">
                      <div>
                        <span className="font-bold text-orange-600">
                          {t("cannedBottledFruits.products.labels.appearance")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {t(
                            `cannedBottledFruits.products.${products[activeProduct].key}.appearance`
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-orange-600">
                          {t("cannedBottledFruits.products.labels.aroma")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {t(
                            `cannedBottledFruits.products.${products[activeProduct].key}.aroma`
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-orange-600">
                          {t("cannedBottledFruits.products.labels.flavour")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {t(
                            `cannedBottledFruits.products.${products[activeProduct].key}.flavour`
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="font-bold text-orange-600">
                          {t("cannedBottledFruits.products.labels.texture")}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300 ml-2">
                          {t(
                            `cannedBottledFruits.products.${products[activeProduct].key}.texture`
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Ingredients */}
                    <div className="bg-orange-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                      <h4 className="font-bold text-orange-600 dark:text-orange-400 mb-2">
                        {t("cannedBottledFruits.products.labels.ingredients")}
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        {t(
                          `cannedBottledFruits.products.${products[activeProduct].key}.ingredients`
                        )}
                      </p>
                    </div>

                    {/* Process */}
                    <div className="bg-blue-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                      <h4 className="font-bold text-blue-600 dark:text-blue-400 mb-2">
                        {t("cannedBottledFruits.products.labels.process")}
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        {t("cannedBottledFruits.products.labels.processDesc")}
                      </p>
                    </div>

                    {/* Best Uses */}
                    <div className="mb-6">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        {t("cannedBottledFruits.products.labels.bestUses")}
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {getProductBestUses(products[activeProduct].key).map(
                          (use, i) => (
                            <div
                              key={i}
                              className="flex items-center space-x-2 bg-gradient-to-r from-orange-100 to-pink-100 dark:from-orange-900/20 dark:to-pink-900/20 rounded-lg p-3"
                            >
                              <FaCheckCircle className="text-orange-600 flex-shrink-0" />
                              <span className="text-gray-700 dark:text-gray-300 text-sm">
                                {use}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {/* Common Specs */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("cannedBottledFruits.products.labels.origin")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "cannedBottledFruits.products.labels.originValue"
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {t("cannedBottledFruits.products.labels.shelfLife")}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">
                            {t(
                              "cannedBottledFruits.products.labels.shelfLifeValue"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Process Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white shadow-xl"
            >
              <h3 className="text-2xl font-bold mb-4">
                {t("cannedBottledFruits.process.title")}
              </h3>
              <p className="text-lg leading-relaxed">
                {t("cannedBottledFruits.process.description")}
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("cannedBottledFruits.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className = "text-2xl";
                          fallback.textContent = cert.icon;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quality & Safety */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("cannedBottledFruits.quality.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {qualityItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {t(`cannedBottledFruits.quality.${item.key}.title`)}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t(`cannedBottledFruits.quality.${item.key}.desc`)}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Serving & Storage */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                {t("cannedBottledFruits.serving.title")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {servingItems.map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="text-5xl text-orange-600 mb-4 flex justify-center">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                      {t(`cannedBottledFruits.serving.${item.key}.title`)}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      {t(`cannedBottledFruits.serving.${item.key}.text`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("cannedBottledFruits.faqs.title")}
              </h2>
              <div className="space-y-4">
                {faqKeys.map((faqKey, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-amber-50 dark:bg-gray-700 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() =>
                        setOpenFaq(openFaq === index ? null : index)
                      }
                      className="w-full text-left p-6 flex justify-between items-center hover:bg-amber-100 dark:hover:bg-gray-600 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white pr-4">
                        {t(`cannedBottledFruits.faqs.questions.${faqKey}.q`)}
                      </h3>
                      <motion.div
                        animate={{ rotate: openFaq === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FaCheckCircle className="text-amber-600 text-2xl flex-shrink-0" />
                      </motion.div>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{ height: openFaq === index ? "auto" : 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-gray-700 dark:text-gray-300">
                        {t(`cannedBottledFruits.faqs.questions.${faqKey}.a`)}
                      </p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("cannedBottledFruits.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("cannedBottledFruits.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>{t("cannedBottledFruits.technical.downloadButton")}</span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default CannedBottledFruits;
